import { useEffect, useState } from 'react';
import { t } from 'i18next';
/**
 * Shared set of recently duplicated manipulative IDs.
 * GameController adds IDs here; the hook reads and clears them on mount.
 * This avoids event timing issues — the ID is in the set BEFORE the component mounts.
 */
export var pendingDuplicateIds = new Set();
/**
 * Call this from GameController after duplicating to register the new ID.
 */
export function markAsDuplicated(id) {
    pendingDuplicateIds.add(id);
}
/** Shared set of recently created manipulative IDs. */
export var pendingCreatedIds = new Set();
export function markAsCreated(id) {
    pendingCreatedIds.add(id);
}
/**
 * Hook that temporarily overrides aria-label to "{name} added to workspace"
 * when the manipulative is freshly created. Same pattern as useDuplicateAnnounce.
 */
export function useCreationAnnounce(id, name) {
    var _a = useState(function () {
        if (pendingCreatedIds.has(id)) {
            pendingCreatedIds.delete(id);
            return t('text.manipulativeAdded', { name: name });
        }
        return null;
    }), overrideLabel = _a[0], setOverrideLabel = _a[1];
    useEffect(function () {
        if (overrideLabel) {
            var timer_1 = setTimeout(function () { return setOverrideLabel(null); }, 500);
            return function () { return clearTimeout(timer_1); };
        }
    }, [overrideLabel]);
    return overrideLabel;
}
/**
 * Reusable hook that temporarily overrides a manipulative's aria-label
 * to "{name} is duplicated successfully" when it is freshly duplicated.
 *
 * Usage:
 *   const duplicateLabel = useDuplicateAnnounce(id, t('text.dice'));
 *   <div aria-label={duplicateLabel ?? originalLabel} />
 */
export function useDuplicateAnnounce(id, name) {
    var _a = useState(function () {
        // Check on initial render (mount) — synchronous, no timing issues
        if (pendingDuplicateIds.has(id)) {
            pendingDuplicateIds.delete(id);
            return t('text.duplicatedAnnounce', { name: name });
        }
        return null;
    }), overrideLabel = _a[0], setOverrideLabel = _a[1];
    useEffect(function () {
        if (overrideLabel) {
            var timer_2 = setTimeout(function () { return setOverrideLabel(null); }, 500);
            return function () { return clearTimeout(timer_2); };
        }
    }, [overrideLabel]);
    return overrideLabel;
}
